import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {AuthUser} from '../types/Auth';
import authService from '../services/authService';
import {useFavourites} from './FavouritesContext';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const { loadFavourites, mergeFavourites, clearFavourites } = useFavourites();

  useEffect(() => {
    // Check for existing authentication on app load
    const initializeAuth = async () => {
      if (authService.isAuthenticated()) {
        const currentUser = authService.getCurrentUser();
        setUser(currentUser);
        await loadFavourites(true);
      } else {
        await loadFavourites(false);
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    await authService.login(email, password);
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    await mergeFavourites();
    await loadFavourites(true);
  };

  const logout = (): void => {
    authService.logout();
    setUser(null);
    clearFavourites();
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
